import { ArchivedCompanyNotice } from '@/components/archived-company-notice';
import { WizardBreadcrumb } from '@/components/breadcrumbs';
import { MatrixWizard } from '@/components/wizard/matrix-wizard';
import { companiesApi, matricesApi, meApi } from '@/lib/api';
import { canManageCompanies } from '@/lib/company-permissions';
import { useEffectiveRole } from '@/lib/effective-role';
import { type WizardSearch, wizardSearchSchema } from '@/lib/wizard-search';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

export const Route = createFileRoute('/wizard/$matrixId')({
  validateSearch: (search): WizardSearch => wizardSearchSchema.parse(search),
  component: WizardPage,
  staticData: { breadcrumb: WizardBreadcrumb },
});

/**
 * The wizard route: resumes a matrix (created from its company's space) by
 * fetching the matrix, its actual owning company (by `matrix.companyId` —
 * not "the sole company", now that multiple companies are real), and the
 * current user in parallel. A 404 here is a hard error — a matrix is always
 * created against a real company, so this shouldn't happen in normal use.
 *
 * Polls the matrix every 3s while it's génération_en_cours — mirrors
 * `matrices.$matrixId.tsx`'s own polling of its `brouillon` state — so
 * `MatrixWizard`'s locked récapitulatif notices completion/failure without
 * a manual refresh.
 *
 * The moment that poll sees `state` flip to `généré`, this route hands off
 * to `/matrices/$matrixId` (a `useEffect`, not the loader — the wizard has
 * to stay mounted through the transition to notice it via its own polling
 * `matrixQuery`, unlike a fresh navigation) — mirroring, in the opposite
 * direction, `matrices.$matrixId.tsx`'s own redirect back into the wizard
 * for anything short of généré. Without this, a finished generation left
 * the user stuck looking at the plain editable récapitulatif with no
 * indication anything happened, free to click "Générer" again and kick off
 * a brand-new run over the matrix that just finished.
 *
 * When the company is archived (issue #22) the wizard stays on screen, read-only
 * (`MatrixWizard` reads `company.archivedAt`), under the same « Cette entreprise
 * est archivée » notice as the company and matrix pages, with « Restaurer » for
 * the roles that may. The company is one of the queries the page already waits
 * for, so the notice is there from the first paint; restoring refreshes that
 * query and the wizard is editable again without a reload.
 */
function WizardPage() {
  const { matrixId } = Route.useParams();
  const { derivedFrom, pivotReset } = Route.useSearch();
  const navigate = useNavigate();

  const matrixQuery = useQuery({
    queryKey: ['matrix', matrixId],
    queryFn: () => matricesApi.get(matrixId),
    refetchInterval: (query) => (query.state.data?.state === 'génération_en_cours' ? 3000 : false),
  });

  useEffect(() => {
    if (matrixQuery.data?.state === 'généré') {
      void navigate({ to: '/matrices/$matrixId', params: { matrixId }, replace: true });
    }
  }, [matrixQuery.data?.state, matrixId, navigate]);

  const companyQuery = useQuery({
    queryKey: ['company', matrixQuery.data?.companyId],
    queryFn: () => companiesApi.getById(matrixQuery.data?.companyId ?? ''),
    enabled: matrixQuery.data !== undefined,
  });
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => meApi.get(),
  });
  const effectiveRole = useEffectiveRole(meQuery.data);

  if (matrixQuery.isError) {
    return (
      <div className="mx-auto max-w-4xl px-6">
        <section className="space-y-2">
          <h1 className="font-bold text-danger text-h3">Matrice introuvable</h1>
          <p className="text-gray-600 text-small">
            Cette matrice n'existe pas ou n'a pas pu être chargée.
          </p>
        </section>
      </div>
    );
  }

  if (!matrixQuery.data || !companyQuery.data || !meQuery.data) {
    return (
      <div className="mx-auto max-w-4xl px-6">
        <p className="text-gray-500 text-small">Chargement…</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col items-center">
      {companyQuery.data.archivedAt && (
        <div className="w-[min(90vw,1200px)] shrink-0 pt-6">
          <ArchivedCompanyNotice
            companyId={companyQuery.data.id}
            canRestore={canManageCompanies(effectiveRole)}
            layout="banner"
          />
        </div>
      )}
      <div className="flex min-h-0 w-full flex-1 flex-col items-center">
        <MatrixWizard
          matrix={matrixQuery.data}
          company={companyQuery.data}
          me={meQuery.data}
          derivedFrom={derivedFrom}
          pivotReset={pivotReset}
        />
      </div>
    </div>
  );
}
